import { createContext, useReducer, useContext, ReactNode } from "react";
import { ConnectionMapping, NewConnection, RunFrequency, synchronisationSchema } from "../types";

const initialConnectionData: NewConnection = {
  name: "",
  sourceAdministrationId: 0,
  destinationAdministrationId: 0,
  systemAuthorizationId: 0,
  enableSyncDebiteuren: false,
  enableSyncGrootboeken: false,
  enableSyncKostenplaatsen: false,
  mappings: [],
  synchronisationSchema: {
    runFrequency: RunFrequency.WEEKLY,
    runDay: 0,
    runHour: 0,
    runMinute: 0,
    lookbackWeeks: 12,
    dryRun: false,
    isPaused: true,
  },
};

// Define action types
type NewConnectionAction =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  | { type: "UPDATE_FIELD"; key: keyof NewConnection; value: any }
  | { type: "APPEND_MAPPINGS"; mappings: ConnectionMapping }
  | { type: "SET_ADMINISTRATIONS"; easyflexId: number; destinationId: number }
  | { type: "SET_SYNCHRONISATION_TIME"; synchronisationTime: synchronisationSchema }
  | { type: "LOAD_EXISTING_CONNECTION"; payload: NewConnection }
  | { type: "RESET" };

// Reducer function
const newConnectionReducer = (state: NewConnection, action: NewConnectionAction): NewConnection => {
  let newState;

  switch (action.type) {
    case "UPDATE_FIELD":
      newState = { ...state, [action.key]: action.value };
      break;
    case "APPEND_MAPPINGS":
      newState = {
        ...state,
        mappings: [...state.mappings, action.mappings]
      };
      break;
    case "SET_ADMINISTRATIONS":
      newState = {
        ...state,
        sourceAdministrationId: action.easyflexId,
        destinationAdministrationId: action.destinationId
      };
      break;
    case "SET_SYNCHRONISATION_TIME":
      newState = { ...state, synchronisationSchema: action.synchronisationTime };
      break;
    case "RESET":
      return initialConnectionData;
    case "LOAD_EXISTING_CONNECTION":
      return action.payload;
    default:
      return state;
  }

  return newState;
};

interface NewConnectionContextType {
  newConnectionData: NewConnection;
  dispatch: React.Dispatch<NewConnectionAction>;
  clearStoredConnectionData: () => void;
}

// Create Context
const NewConnectionContext = createContext<NewConnectionContextType | undefined>(undefined);

// Provider Component
export const NewConnectionProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(newConnectionReducer, initialConnectionData);

  const clearStoredConnectionData = () => {
    dispatch({ type: "RESET" });
  };

  return (
    <NewConnectionContext.Provider value={{ newConnectionData: state, dispatch, clearStoredConnectionData }}>
      {children}
    </NewConnectionContext.Provider>
  );
};

// Custom Hook
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with the component or provider that uses it
export const useNewConnectionContext = () => {
  const context = useContext(NewConnectionContext);
  if (!context) {
    throw new Error("useNewConnectionContext must be used within a NewConnectionProvider");
  }
  return context;
};
